import React, { useState, useEffect, useRef, useMemo } from "react";
import { PropTypes } from "prop-types";
import { formatDate2string } from "../util/index.js";
import DatePicker from "react-datepicker";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment";
import axios from "axios";
import FunnelChart from "../components/charts/FunnelChart.js";
import StepDropOffChart from "../components/charts/StepDropOffChart.js";
import ConversionTrendChart from "../components/charts/ConversionTrendChart.js";
import DropOffTrendChart from "../components/charts/DropOffTrendChart.js";
import {
  fetchFunnelForDay,
  fetchFunnelTrend,
  computeConversionRate,
  computeDropOff,
} from "../services/funnelReport.js";
import "../styles/funnelDashboard.css";

// Funnel transitions used to surface the single worst drop-off as a KPI.
const KPI_TRANSITIONS = [
  { from: "step0", to: "step0_5", label: "After entering" },
  { from: "step0_5", to: "step1", label: "After contact" },
  { from: "step1", to: "step2", label: "After info" },
  { from: "step2", to: "step3", label: "After slot picked" },
  { from: "step3", to: "completed", label: "After review" },
];

// Report range is capped at 31 days (inclusive). Wider ranges fan out into too
// many per-day queries on the backend and risk a timeout while the Excel builds.
const MAX_RANGE_DAYS = 31;

function IncompleteAppointments({ appState }) {
  const today = new Date();
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [locations, setLocations] = useState([]);
  const [selection, setSelection] = useState({});
  const [includeAppointments, setIncludeAppointments] = useState(false);
  // Which download is in flight: "report" | "recovery" | null (for per-button spinner).
  const [downloadKind, setDownloadKind] = useState(null);
  const [state, setState] = useState({
    message: "",
    status: "ready",
    success: false,
    show: false,
  });
  // Funnel + 7-day trend charts
  const [funnel, setFunnel] = useState({ counts: null, loading: false, error: null });
  const [trend, setTrend] = useState({ days: null, loading: false, error: null });
  const fetchSeqRef = useRef(0);

  const getLocations = async () => {
    try {
      const getMethod = {
        method: "GET",
        headers: {
          "Content-type": "application/json; charset=UTF-8",
          siteid: appState.nav.siteId,
          authorization: appState.singleAuth.token,
        },
      };
      const getResponse = await fetch(
        `${process.env.REACT_APP_API_URL}/api/sites/all/locations/`,
        getMethod
      );
      const locationData = await getResponse.json();

      if (getResponse.ok && getResponse.status === 200) {
        setLocations(locationData.siteslist);
        const firstSite = locationData.siteslist[0].id;
        const value = firstSite.split("-");
        const siteId = value[0];
        const locationId = value[1];
        setSelection((selection) => ({
          ...selection,
          siteId: siteId,
          locationId: locationId,
          locationName: locationData.siteslist[0].name,
        }));
      } else {
        setLocations([]);
        setState((state) => ({
          ...state,
          show: true,
          status: "ready",
          message: locationData.message ?? JSON.stringify(locationData),
          success: false,
        }));
      }
    } catch (error) {
      console.error(error);
      setLocations([]);
      setState((state) => ({
        ...state,
        show: true,
        status: "ready",
        message: JSON.stringify(error),
        success: false,
      }));
    }
  };

  const handleLocationChange = (event) => {
    let { value } = event.target;
    const locationItem = locations.find((item) => item.id === value);
    value = value.split("-");
    const siteId = value[0];
    const locationId = value[1];

    setSelection((selection) => ({
      ...selection,
      siteId: event.target.value === "0-0" ? "0" : siteId,
      locationId: locationId,
      locationName: locationItem.name,
    }));
  };

  // Keep the range valid whenever the start date moves: the end date can never
  // be before the start, nor more than 31 days (inclusive) after it.
  const handleStartDateChange = (date) => {
    setStartDate(date);
    const maxEnd = moment(date)
      .add(MAX_RANGE_DAYS - 1, "days")
      .toDate();
    setEndDate((prevEnd) => {
      if (moment(prevEnd).isBefore(moment(date), "day")) return date;
      if (moment(prevEnd).isAfter(moment(maxEnd), "day")) return maxEnd;
      return prevEnd;
    });
  };

  const handleButtonDownloadReport = async (retargetOnly = false) => {
    try {
      const start = moment(startDate).startOf("day");
      const end = moment(endDate).startOf("day");

      // Validate the range client-side before hitting the backend.
      if (end.isBefore(start, "day")) {
        setState((state) => ({
          ...state,
          show: true,
          status: "ready",
          message: "The end date must be on or after the start date.",
          success: false,
        }));
        return;
      }
      const spanDays = end.diff(start, "days") + 1;
      if (spanDays > MAX_RANGE_DAYS) {
        setState((state) => ({
          ...state,
          show: true,
          status: "ready",
          message: `The date range can't be longer than ${MAX_RANGE_DAYS} days.`,
          success: false,
        }));
        return;
      }

      setState((state) => ({ ...state, status: "loading" }));
      setDownloadKind(retargetOnly ? "recovery" : "report");
      const startParam = start.format("YYYYMMDD").toString();
      const endParam = end.format("YYYYMMDD").toString();
      const startName = start.format("YYYY-MM-DD").toString();
      const endName = end.format("YYYY-MM-DD").toString();
      const isSingleDay = startParam === endParam;
      const siteId = selection.siteId;
      // Lead-recovery always includes converted (booked) leads so the report
      // shows the full sent -> opened -> completed journey.
      const booked = retargetOnly ? true : includeAppointments;
      const rangeParams = isSingleDay
        ? `date=${startParam}`
        : `startDate=${startParam}&endDate=${endParam}`;
      const dateName = isSingleDay ? startName : `${startName}_to_${endName}`;
      const filePrefix = retargetOnly ? "leadrecovery" : "incompletebooking";
      const requestSCV = `api/incomplete/booking?${rangeParams}&siteId=${siteId}&booked=${booked}&format=excel${
        retargetOnly ? "&retargetOnly=true" : ""
      }`;

      axios({
        method: "get",
        url: `${process.env.REACT_APP_API_URL}/${requestSCV}`,
        responseType: "blob",
      })
        .then(function (response) {
          try {
            if (response.status === 200) {
              const url = window.URL.createObjectURL(response.data);
              const link = document.createElement("a");
              link.href = url;
              link.setAttribute(
                "download",
                `${filePrefix}-${selection.locationName}-${dateName}.xlsx`
              );
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              URL.revokeObjectURL(url);
              setState((state) => ({
                ...state,
                show: true,
                status: "ready",
                message: "The report has been successfully downloaded",
                success: true,
              }));
            }
          } catch (errorE) {
            console.error(errorE);
            setState((state) => ({
              ...state,
              show: true,
              status: "ready",
              message: "The file couldn't be fetched: " + JSON.stringify(errorE),
              success: false,
            }));
          }
        })
        .catch(async (error) => {
          try {
            const { response } = error;
            const text = await response.data.text();
            const jsonResponse = await JSON.parse(text);
            setState((state) => ({
              ...state,
              show: true,
              status: "ready",
              message: jsonResponse.message ?? "No data available for download",
              success: false,
            }));
          } catch (errorE) {
            console.error(errorE);
            setState((state) => ({
              ...state,
              show: true,
              status: "ready",
              message: "The file couldn't be fetched: " + JSON.stringify(error),
              success: false,
            }));
          }
        })
        .finally(() => setDownloadKind(null));
    } catch (error) {
      console.error(error);
      setDownloadKind(null);
      setState((state) => ({
        ...state,
        show: true,
        status: "ready",
        message: "The file couldn't be fetched: " + JSON.stringify(error),
        success: false,
      }));
    }
  };

  useEffect(() => {
    getLocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setState((state) => ({ ...state, show: false, message: "" }));
    }, 9000);
    return () => clearTimeout(timer);
  }, [state.show]);

  // Fetch funnel + trend whenever site or date changes. Debounced 400ms so
  // dragging the DatePicker quickly doesn't flood the backend.
  useEffect(() => {
    const siteId = selection.siteId;
    if (!siteId) return;
    const seq = ++fetchSeqRef.current;
    setFunnel((s) => ({ ...s, loading: true, error: null }));
    setTrend((s) => ({ ...s, loading: true, error: null }));

    const token = appState?.singleAuth?.token;
    const handle = setTimeout(async () => {
      try {
        const dayData = await fetchFunnelForDay({ date: startDate, siteId, token });
        if (seq !== fetchSeqRef.current) return;
        setFunnel({ counts: dayData.counts, loading: false, error: null });
      } catch (err) {
        if (seq !== fetchSeqRef.current) return;
        setFunnel({ counts: null, loading: false, error: err?.message || "Network error" });
      }
      try {
        const trendData = await fetchFunnelTrend({
          endDate: startDate,
          days: 7,
          siteId,
          token,
        });
        if (seq !== fetchSeqRef.current) return;
        setTrend({ days: trendData.days, loading: false, error: null });
      } catch (err) {
        if (seq !== fetchSeqRef.current) return;
        setTrend({ days: null, loading: false, error: err?.message || "Network error" });
      }
    }, 400);

    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selection.siteId, startDate]);

  // KPI summary derived from the day's funnel counts.
  const kpis = useMemo(() => {
    const c = funnel.counts;
    if (!c) return null;
    const conversion = computeConversionRate(c);
    let worst = null;
    for (const t of KPI_TRANSITIONS) {
      const d = computeDropOff(c[t.from], c[t.to]);
      if (d != null && (worst == null || d > worst.value)) {
        worst = { value: d, label: t.label };
      }
    }
    return { entered: c.step0, booked: c.completed, conversion, worst };
  }, [funnel.counts]);

  // Two retargeting funnels, tracked independently: the 45-min resume SMS and
  // the 48h discount SMS. Each: SMS sent -> opened link -> completed booking.
  // convRate is completed/sent (end-to-end conversion of the SMS).
  const retarget = useMemo(() => {
    const r = funnel.counts && funnel.counts.retarget;
    if (!r) return null;
    const funnelOf = (sent, opened, completed) => ({
      sent,
      opened,
      completed,
      openRate: sent ? (opened / sent) * 100 : null,
      convRate: sent ? (completed / sent) * 100 : null,
    });
    return {
      resume: funnelOf(r.resumeSmsSent || 0, r.resumeOpened || 0, r.resumeCompleted || 0),
      offer: funnelOf(r.offerSent || 0, r.offerOpened || 0, r.offerCompleted || 0),
    };
  }, [funnel.counts]);

  const dateLabel = moment(startDate).format("MMM D, YYYY");
  const locationName = selection.locationName || "All locations";

  // One retargeting funnel card (SMS sent -> opened -> booked + conversion %).
  const renderRetarget = (title, sub, f) => (
    <div className="lb-card">
      <div className="lb-card__head">
        <h2 className="lb-card__title">{title}</h2>
        <p className="lb-card__sub">{sub}</p>
      </div>
      <div className="lb-card__body">
        <div className="lb-kpis">
          <div className="lb-card lb-kpi">
            <div className="lb-kpi__label">SMS sent</div>
            <div className="lb-kpi__value">{f ? f.sent : "—"}</div>
            <div className="lb-kpi__hint">Recovery links sent</div>
          </div>
          <div className="lb-card lb-kpi">
            <div className="lb-kpi__label">Opened</div>
            <div className="lb-kpi__value">{f ? f.opened : "—"}</div>
            <div className="lb-kpi__hint">
              {f && f.openRate != null ? `${f.openRate.toFixed(0)}% open rate` : "opened link"}
            </div>
          </div>
          <div className="lb-card lb-kpi">
            <div className="lb-kpi__label">Booked</div>
            <div className="lb-kpi__value lb-kpi__value--ok">{f ? f.completed : "—"}</div>
            <div className="lb-kpi__hint">
              {f && f.convRate != null ? `${f.convRate.toFixed(1)}% conversion` : "booked from link"}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="lb-funnel" data-cy="incomplete-appointments">
      <div className="lb-funnel__inner">
        {/* Header */}
        <div className="lb-funnel__header">
          <div>
            <h1 className="lb-funnel__title">Incomplete Booking Funnel</h1>
            <p className="lb-funnel__subtitle">
              Where prospective clients drop off before booking — across the web
              site and the booking widget.
            </p>
          </div>
          <span className="lb-funnel__today">Today · {formatDate2string(today)}</span>
        </div>

        {/* Controls */}
        <div className="lb-card lb-controls">
          <div className="lb-field lb-field--grow">
            <label className="lb-field__label" htmlFor="location-select">
              Site
            </label>
            <select
              id="location-select"
              data-cy="location-select"
              className="form-select"
              value={selection.siteId + "-" + selection.locationId}
              onChange={handleLocationChange}
            >
              {locations.map((option) => (
                <option value={option.id} key={option.id}>
                  {option.name}
                </option>
              ))}
            </select>
          </div>

          <div className="lb-field" data-cy="start-date">
            <label className="lb-field__label">Start date</label>
            <DatePicker
              selected={startDate}
              onChange={handleStartDateChange}
              selectsStart
              startDate={startDate}
              endDate={endDate}
              value={startDate}
            />
          </div>

          <div className="lb-field" data-cy="end-date">
            <label className="lb-field__label">End date</label>
            <DatePicker
              selected={endDate}
              onChange={(date) => setEndDate(date)}
              selectsEnd
              startDate={startDate}
              endDate={endDate}
              minDate={startDate}
              maxDate={moment(startDate)
                .add(MAX_RANGE_DAYS - 1, "days")
                .toDate()}
              value={endDate}
            />
          </div>

          <div className="lb-field">
            <label className="lb-field__label">Report option</label>
            <div className="lb-switch-row">
              <input
                data-cy="btn-switch-include-appoiments"
                onChange={(e) => setIncludeAppointments(e.target.checked)}
                className="form-check-input"
                type="checkbox"
                role="switch"
                id="flexSwitchCheckDefault"
                checked={includeAppointments}
              />
              <span className="lb-switch-help">
                Include leads that booked later (booked + incomplete)
              </span>
            </div>
          </div>

          <div className="lb-field">
            <label className="lb-field__label" aria-hidden="true">
              &nbsp;
            </label>
            <button
              className="lb-btn"
              data-cy="btn-download-incomplete-appointments"
              onClick={() => handleButtonDownloadReport(false)}
              disabled={downloadKind !== null}
            >
              {downloadKind === "report" ? (
                <FontAwesomeIcon spin icon={faSpinner} />
              ) : (
                "Download Report"
              )}
            </button>
          </div>

          <div className="lb-field">
            <label className="lb-field__label" aria-hidden="true">
              &nbsp;
            </label>
            <button
              className="lb-btn lb-btn--secondary"
              data-cy="btn-download-lead-recovery"
              onClick={() => handleButtonDownloadReport(true)}
              disabled={downloadKind !== null}
              title="Only leads that received a retargeting SMS (resume or discount)"
            >
              {downloadKind === "recovery" ? (
                <FontAwesomeIcon spin icon={faSpinner} />
              ) : (
                "Download Lead Recovery"
              )}
            </button>
          </div>
        </div>

        {state.show && (
          <div
            data-cy="alert-message"
            className={
              state.success
                ? "lb-alert alert alert-success"
                : "lb-alert alert alert-danger"
            }
            role="alert"
          >
            {state.message}
          </div>
        )}

        <div className="lb-note">
          The downloaded report covers the full <strong>Start date → End date</strong>{" "}
          range (max&nbsp;{MAX_RANGE_DAYS} days); the funnel and trend charts below
          reflect the start date. “Booked” counts leads that completed a booking
          from either front-end: the web site (reaches step&nbsp;4) or the booking
          widget (stamped with a booking date by the appointments webhook).
        </div>

        {/* Retargeting conversion — the two recovery SMS funnels, shown first */}
        <div className="lb-card__head" style={{ marginBottom: 4 }}>
          <h2 className="lb-card__title">Lead recovery — conversion</h2>
          <p className="lb-card__sub">
            Two SMS touchpoints, tracked independently · leads created on {dateLabel}
          </p>
        </div>
        <div className="lb-grid-2">
          {renderRetarget(
            "Resume SMS · 45 min",
            "Continue-booking link — no discount",
            retarget && retarget.resume
          )}
          {renderRetarget(
            "Discount SMS · 48 h",
            "15% off link · sent → opened → booked",
            retarget && retarget.offer
          )}
        </div>

        {/* KPI cards */}
        <div className="lb-kpis lb-section-gap">
          <div className="lb-card lb-kpi">
            <div className="lb-kpi__label">Entered funnel</div>
            <div className="lb-kpi__value">{kpis ? kpis.entered : "—"}</div>
            <div className="lb-kpi__hint">{dateLabel}</div>
          </div>
          <div className="lb-card lb-kpi">
            <div className="lb-kpi__label">Booked</div>
            <div className="lb-kpi__value lb-kpi__value--brand">
              {kpis ? kpis.booked : "—"}
            </div>
            <div className="lb-kpi__hint">Web + widget</div>
          </div>
          <div className="lb-card lb-kpi">
            <div className="lb-kpi__label">Conversion</div>
            <div
              className={
                "lb-kpi__value " +
                (kpis && kpis.conversion >= 20
                  ? "lb-kpi__value--ok"
                  : "lb-kpi__value--bad")
              }
            >
              {kpis ? `${kpis.conversion.toFixed(1)}%` : "—"}
            </div>
            <div className="lb-kpi__hint">Booked / entered</div>
          </div>
          <div className="lb-card lb-kpi">
            <div className="lb-kpi__label">Biggest drop-off</div>
            <div className="lb-kpi__value lb-kpi__value--bad">
              {kpis && kpis.worst ? `${kpis.worst.value.toFixed(0)}%` : "—"}
            </div>
            <div className="lb-kpi__hint">
              {kpis && kpis.worst ? kpis.worst.label : "—"}
            </div>
          </div>
        </div>

        {/* Funnel + step drop-off, side by side on wide screens */}
        <div className="lb-grid-2">
          <div className="lb-card">
            <div className="lb-card__head">
              <h2 className="lb-card__title">Booking funnel</h2>
              <p className="lb-card__sub">
                {locationName} · {dateLabel}
              </p>
            </div>
            <div className="lb-card__body">
              <FunnelChart
                counts={funnel.counts}
                loading={funnel.loading}
                error={funnel.error}
              />
            </div>
          </div>

          <div className="lb-card">
            <div className="lb-card__head">
              <h2 className="lb-card__title">Drop-off after each main step</h2>
              <p className="lb-card__sub">% of people lost at each transition</p>
            </div>
            <div className="lb-card__body">
              <StepDropOffChart
                counts={funnel.counts}
                loading={funnel.loading}
                error={funnel.error}
              />
            </div>
          </div>
        </div>

        {/* Trends */}
        <div className="lb-card lb-section-gap">
          <div className="lb-card__head">
            <h2 className="lb-card__title">Conversion — last 7 days</h2>
            <p className="lb-card__sub">{locationName}</p>
          </div>
          <div className="lb-card__body">
            <ConversionTrendChart
              days={trend.days}
              loading={trend.loading}
              error={trend.error}
            />
          </div>
        </div>

        <div className="lb-card lb-section-gap">
          <div className="lb-card__head">
            <h2 className="lb-card__title">Drop-off by step — last 7 days</h2>
            <p className="lb-card__sub">{locationName}</p>
          </div>
          <div className="lb-card__body">
            <DropOffTrendChart
              days={trend.days}
              loading={trend.loading}
              error={trend.error}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

IncompleteAppointments.propTypes = {
  appState: PropTypes.object,
  setAppState: PropTypes.func,
};

export default IncompleteAppointments;
